import { lazy, Suspense, useEffect, useState } from 'react';

/// Components
import Index from './jsx/index';
import { connect, useDispatch } from 'react-redux';

import {
    Route,
    Routes,
    Navigate,
    useLocation,
    useNavigate,
    useParams
} from 'react-router-dom';

/// Authentication
import { checkAutoLogin } from './services/AuthService';
import { isAuthenticated } from './store/selectors/AuthSelectors';

/// Styles
import './vendor/swiper/css/swiper-bundle.min.css';
import './vendor/bootstrap-select/dist/css/bootstrap-select.min.css';
import './css/style.css';

import DeselePendingApproval from './jsx/components/DieselItem/DiseleSuperAdminApproval';

const SignUp = lazy(() => import('./jsx/pages/Registration'));

const Login = lazy(() => {
    return new Promise(resolve => {
        setTimeout(() => resolve(import('./jsx/pages/Login')), 500);
    });
});

function Preloader() {
    return (
        <div id="preloader">
            <div className="sk-three-bounce">
                <div className="sk-child sk-bounce1"></div>
                <div className="sk-child sk-bounce2"></div>
                <div className="sk-child sk-bounce3"></div>
            </div>
        </div>
    );
}

function withRouter(Component) {
    function ComponentWithRouterProp(props) {
        const location = useLocation();
        const navigate = useNavigate();
        const params = useParams();

        return (
            <Component
                {...props}
                router={{ location, navigate, params }}
            />
        );
    }

    return ComponentWithRouterProp;
}

function App(props) {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [authReady, setAuthReady] = useState(false);

    // Restore authentication before rendering routes.
    useEffect(() => {
        let active = true;

        const restoreLogin = async () => {
            try {
                await checkAutoLogin(dispatch, navigate);
            } catch (error) {
                console.error(
                    'Unable to restore authentication:',
                    error
                );
            } finally {
                if (active) {
                    setAuthReady(true);
                }
            }
        };

        restoreLogin();

        return () => {
            active = false;
        };
    }, [dispatch, navigate]);

    // Restore the saved theme.
    useEffect(() => {
        const savedTheme = localStorage.getItem('theme');

        if (savedTheme === 'dark') {
            document.body.classList.add('dark-mode');
        } else {
            document.body.classList.remove('dark-mode');
        }
    }, []);

    // Prevent a temporary unauthenticated state from changing the URL.
    if (!authReady) {
        return <Preloader />;
    }

    if (props.isAuthenticated) {
        return (
            <Suspense fallback={<Preloader />}>
                <Routes>
                    <Route
                        path="/login"
                        element={
                            <Navigate to="/dashboard" replace />
                        }
                    />

                    <Route
                        path="/"
                        element={
                            <Navigate to="/dashboard" replace />
                        }
                    />

                    <Route
                        path="/*"
                        element={<Index />}
                    />
                </Routes>
            </Suspense>
        );
    }

    return (
        <div className="vh-100">
            <Suspense fallback={<Preloader />}>
                <Routes>
                    <Route
                        path="/login"
                        element={<Login />}
                    />

                    <Route
                        path="/page-register"
                        element={<SignUp />}
                    />

                    <Route
                        path="/super-admin/approve"
                        element={<DeselePendingApproval />}
                    />

                    <Route
                        path="*"
                        element={
                            <Navigate to="/login" replace />
                        }
                    />
                </Routes>
            </Suspense>
        </div>
    );
}

const mapStateToProps = (state) => {
    return {
        isAuthenticated: isAuthenticated(state)
    };
};

export default withRouter(connect(mapStateToProps)(App));